import { useEffect, useState } from "react";
import { jobsApi, fleetApi, API } from "@/lib/api";
import { JOB_STATUS_CONFIG, PRIORITY_CONFIG } from "@/lib/constants";
import { Plus, ClipboardText, ArrowRight, CalendarBlank, Truck as TruckIcon, FilePdf, Download, Camera, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import JobFormDialog from "@/components/jobs/JobFormDialog";
import { LLSelect } from "@/components/ui/ll-select";
import PhotoGallery from "@/components/photos/PhotoGallery";
import BridgeGateBanner from "@/components/jobs/BridgeGateBanner";
import ReportBuilderDialog from "@/components/reports/ReportBuilderDialog";

const COLUMNS = ["scheduled", "dispatched", "in_progress", "completed"];

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openReport, setOpenReport] = useState(false);
  const [view, setView] = useState("board"); // 'board' | 'list'

  const load = async () => {
    try {
      const [j, a] = await Promise.all([jobsApi.list(), fleetApi.list()]);
      setJobs(j);
      setAssets(a);
    } catch (e) {
      toast.error("Failed to load jobs");
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (data) => {
    try {
      await jobsApi.create(data);
      toast.success("Job dispatched");
      setOpenForm(false);
      load();
    } catch (e) {
      toast.error("Failed to create job");
    }
  };

  const advanceStatus = async (job) => {
    const idx = COLUMNS.indexOf(job.status);
    const next = idx >= 0 && idx < COLUMNS.length - 1 ? COLUMNS[idx + 1] : null;
    if (!next) return;
    try {
      await jobsApi.update(job.id, { status: next });
      toast.success(`Job ${job.job_number} → ${JOB_STATUS_CONFIG[next].label}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "bridge_formula_block") {
        toast.error("Blocked by Federal Bridge Formula — acknowledge override below to dispatch");
      } else {
        toast.error("Failed to update job");
      }
    }
  };

  const assignAsset = async (jobId, assetId) => {
    try {
      await jobsApi.update(jobId, { asset_id: assetId, status: "dispatched" });
      toast.success("Asset assigned & dispatched");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "bridge_formula_block") {
        toast.error("Dispatch blocked: Federal Bridge violation — acknowledge override on the job card");
      } else {
        toast.error("Failed to assign asset");
      }
    }
  };

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col" data-testid="jobs-page">
      <div className="border-b border-[#2a2a2a] p-4 lg:p-6 flex items-center gap-3 bg-[#0d0d0d]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Dispatch Board</div>
          <h2 className="font-heading font-black text-xl tracking-tight">
            {jobs.length} Active Jobs
          </h2>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpenReport(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid="daily-report-btn"
          >
            <FilePdf size={16} weight="bold" />
            Daily Report
          </button>

          <div className="flex border border-[#2a2a2a]">
            <button
              onClick={() => setView("board")}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${
                view === "board" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"
              }`}
              data-testid="view-board"
            >
              Board
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${
                view === "list" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"
              }`}
              data-testid="view-list"
            >
              List
            </button>
          </div>

          <button
            onClick={() => setOpenForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid="new-job-btn"
          >
            <Plus size={16} weight="bold" />
            New Job
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 lg:p-6">
        {view === "board" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {COLUMNS.map((status) => {
              const cfg = JOB_STATUS_CONFIG[status];
              const colJobs = jobs.filter((j) => j.status === status);
              return (
                <div key={status} className="bg-[#0d0d0d] border border-[#2a2a2a] flex flex-col">
                  <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2" style={{ background: cfg.color }}></span>
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: cfg.color }}>
                        {cfg.label}
                      </span>
                    </div>
                    <span className="font-mono text-xs font-bold text-[#a1a1aa]">
                      {String(colJobs.length).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex-1 p-2 space-y-2 min-h-[200px]">
                    {colJobs.length === 0 ? (
                      <div className="text-[#71717a] text-[10px] uppercase tracking-[0.2em] p-4 text-center">
                        No jobs
                      </div>
                    ) : colJobs.map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        assets={assets}
                        onAdvance={() => advanceStatus(job)}
                        onAssign={(assetId) => assignAsset(job.id, assetId)}
                        onOverride={load}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-[#2a2a2a] bg-[#0d0d0d] overflow-hidden">
            <table className="w-full" data-testid="jobs-table">
              <thead>
                <tr className="bg-[#1a1a1a] border-b border-[#2a2a2a]">
                  {["Job #", "Customer", "Pickup", "Dropoff", "Asset", "Weight", "Priority", "Status"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#71717a] font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const sCfg = JOB_STATUS_CONFIG[j.status];
                  const pCfg = PRIORITY_CONFIG[j.priority];
                  return (
                    <tr key={j.id} className="border-b border-[#2a2a2a] hover:bg-[#1a1a1a]">
                      <td className="px-4 py-3 font-mono font-bold text-sm">{j.job_number}</td>
                      <td className="px-4 py-3 text-sm">{j.customer}</td>
                      <td className="px-4 py-3 text-xs text-[#a1a1aa]">{j.pickup_address}</td>
                      <td className="px-4 py-3 text-xs text-[#a1a1aa]">{j.dropoff_address}</td>
                      <td className="px-4 py-3 text-xs font-mono">{j.asset_name || "—"}</td>
                      <td className="px-4 py-3 text-xs font-mono">{j.weight_lbs ? `${j.weight_lbs.toLocaleString()} lbs` : "—"}</td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: pCfg.color }}>
                          {pCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                          style={{ background: sCfg.bg, color: sCfg.color, border: `1px solid ${sCfg.color}` }}
                        >
                          {sCfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openForm && (
        <JobFormDialog
          assets={assets}
          onClose={() => setOpenForm(false)}
          onSave={handleSave}
        />
      )}

      {openReport && <ReportBuilderDialog onClose={() => setOpenReport(false)} />}
    </div>
  );
}

function JobCard({ job, assets, onAdvance, onAssign, onOverride }) {
  const pCfg = PRIORITY_CONFIG[job.priority];
  const canAdvance = job.status !== "completed" && job.status !== "cancelled";
  const availableTrucks = assets.filter((a) => a.type === "truck" && a.status !== "maintenance");
  const [showPhotos, setShowPhotos] = useState(false);
  const photoCount = (job.photo_ids || []).length;
  const showBridge = job.weight_lbs > 0 && (job.status === "scheduled" || job.status === "dispatched");

  return (
    <div className="bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#4d4d4d] p-3 transition-colors" data-testid={`job-card-${job.job_number}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="font-mono font-bold text-xs">{job.job_number}</div>
        <span className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: pCfg.color }}>
          ● {pCfg.label}
        </span>
      </div>
      <div className="text-sm font-semibold mb-1 truncate">{job.customer}</div>
      {job.status === "completed" && (
        <a
          href={`${API}/jobs/${job.id}/audit-zip`}
          onClick={(e) => e.stopPropagation()}
          className="text-[9px] uppercase tracking-[0.25em] text-[#22c55e] hover:underline font-mono mb-1 inline-block"
          data-testid={`job-audit-zip-${job.id}`}
          title="Download audit bundle (invoice + POD + DVIRs in one ZIP)"
        >
          ⬇ Audit ZIP
        </a>
      )}
      {job.customer_email && (
        <div className="text-[10px] text-[#22c55e] font-mono mb-1 truncate flex items-center gap-1" title="POD-email enabled for this customer">
          <span>✉</span> {job.customer_email}
        </div>
      )}
      <div className="space-y-1 text-[10px] text-[#a1a1aa] mb-3">
        <div className="flex items-start gap-1">
          <span className="text-[#22c55e] font-mono mt-px">▲</span>
          <span className="truncate">{job.pickup_address}</span>
        </div>
        <div className="flex items-start gap-1">
          <span className="text-[#ff8c00] font-mono mt-px">▼</span>
          <span className="truncate">{job.dropoff_address}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 text-[10px] font-mono text-[#71717a]">
        {job.weight_lbs > 0 && <span>{job.weight_lbs.toLocaleString()} lbs</span>}
        {job.asset_name && (
          <span className="text-[#4682b4] flex items-center gap-1">
            <TruckIcon size={10} /> {job.asset_name}
          </span>
        )}
        <button
          onClick={() => setShowPhotos((s) => !s)}
          className={`ml-auto flex items-center gap-1 px-1.5 py-0.5 transition-colors ${
            photoCount > 0 ? "text-[#ff8c00] border border-[#ff8c00]/30 hover:bg-[#ff8c00]/10" : "text-[#71717a] border border-[#2a2a2a] hover:border-[#4d4d4d]"
          }`}
          data-testid={`photos-toggle-${job.job_number}`}
        >
          <Camera size={10} /> {photoCount}
          {showPhotos ? <CaretUp size={8} /> : <CaretDown size={8} />}
        </button>
      </div>

      {showPhotos && (
        <div className="mb-3 pt-2 border-t border-[#2a2a2a]">
          <PhotoGallery jobId={job.id} compact allowUpload />
        </div>
      )}

      {showBridge && <BridgeGateBanner job={job} compact onOverride={onOverride} />}

      <div className="flex items-center gap-1">
        {!job.asset_id && availableTrucks.length > 0 && (
          <LLSelect
            value=""
            onChange={(v) => v && onAssign(v)}
            placeholder="Assign Truck..."
            testId={`assign-asset-${job.job_number}`}
            className="!w-full flex-1 py-1.5 text-[10px]"
            options={availableTrucks.map((a) => ({ value: a.id, label: a.name }))}
          />
        )}
        {canAdvance && job.asset_id && (
          <button
            onClick={onAdvance}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-[#4682b4]/20 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid={`advance-job-${job.job_number}`}
          >
            Next <ArrowRight size={10} />
          </button>
        )}
        <a
          href={jobsApi.pdfUrl(job.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="px-2 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00] hover:text-[#ff8c00] transition-colors"
          data-testid={`pdf-job-${job.job_number}`}
          title="Download dispatch PDF"
          onClick={(e) => e.stopPropagation()}
        >
          <FilePdf size={12} />
        </a>
      </div>
    </div>
  );
}
